'use client';

import React, { useState, useEffect } from 'react';
import TopNav from '../components/TopNav';
import FooterBar from '../components/FooterBar';
import { Cookie, Settings, BarChart2, Target, CheckCircle } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface ToastProps {
  message: string;
  visible: boolean;
}

function Toast({ message, visible }: ToastProps) {
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-card border border-border rounded-lg px-5 py-3 shadow-xl transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <CheckCircle size={18} className="text-crypto-green flex-shrink-0" />
      <span className="text-sm font-medium text-foreground">{message}</span>
    </div>
  );
}

const cookieTypes = [
  {
    key: 'analytics',
    icon: BarChart2,
    title: 'Analytics Cookies',
    desc: 'These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us to know which pages are the most and least popular.',
    required: false,
  },
  {
    key: 'marketing',
    icon: Target,
    title: 'Marketing Cookies',
    desc: 'These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites.',
    required: false,
  },
  {
    key: 'preference',
    icon: Settings,
    title: 'Preference Cookies',
    desc: 'These cookies enable the website to provide enhanced functionality and personalisation. They may be set by us or by third party providers whose services we have added to our pages.',
    required: false,
  },
];

interface CookieState {
  analytics: boolean;
  marketing: boolean;
  preference: boolean;
}

export default function CookiesPage() {
  const [cookieState, setCookieState] = useState<CookieState>({
    analytics: false,
    marketing: false,
    preference: false,
  });
  const [toast, setToast] = useState<ToastProps>({ message: '', visible: false });

  const showToast = (message: string) => {
    setToast({ message, visible: true });
    setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 3000);
  };

  const handleToggle = (key: keyof CookieState) => {
    setCookieState((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAcceptAll = () => {
    setCookieState({ analytics: true, marketing: true, preference: true });
    showToast('Cookie preferences saved: All accepted');
  };

  const handleEssentialOnly = () => {
    setCookieState({ analytics: false, marketing: false, preference: false });
    showToast('Cookie preferences saved: Essential only');
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />
      <main className="w-full max-w-screen-2xl mx-auto px-4 lg:px-6 xl:px-8 2xl:px-10 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <span className="text-primary text-xs font-bold uppercase tracking-widest">Legal</span>
            <h1 className="text-3xl font-black text-foreground mt-2">Cookie Policy</h1>
            <p className="text-muted-foreground text-sm mt-1">Last updated: September 15, 2026</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-5 mb-6">
            <p className="text-sm text-muted-foreground leading-relaxed">
              DMTV uses cookies and similar tracking technologies to track the activity on our service and hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            </p>
          </div>

          {/* Essential Cookies — always required */}
          <div className="bg-card border border-border rounded-lg p-5 mb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-9 h-9 rounded bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Cookie size={16} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-foreground mb-1">Essential Cookies</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you such as setting your privacy preferences, logging in, or filling in forms.
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded font-semibold">Required</span>
              </div>
            </div>
          </div>

          {/* Interactive cookie toggles */}
          <div className="space-y-4">
            {cookieTypes.map((type) => {
              const Icon = type.icon;
              const isActive = cookieState[type.key as keyof CookieState];
              return (
                <div key={type.key} className="bg-card border border-border rounded-lg p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-9 h-9 rounded bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon size={16} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-foreground mb-1">{type.title}</h2>
                        <p className="text-sm text-muted-foreground leading-relaxed">{type.desc}</p>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => handleToggle(type.key as keyof CookieState)}
                        aria-label={`Toggle ${type.title}`}
                        className={`w-10 h-5 rounded-full relative transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                          isActive ? 'bg-primary' : 'bg-muted'
                        }`}
                      >
                        <div
                          className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-all duration-200 shadow-sm ${
                            isActive ? 'left-5' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={handleAcceptAll}
              className="flex-1 bg-primary hover:bg-primary/90 text-white py-2.5 rounded text-sm font-semibold transition-all duration-150 active:scale-95"
            >
              Accept All Cookies
            </button>
            <button
              onClick={handleEssentialOnly}
              className="flex-1 bg-muted hover:bg-muted/80 text-foreground py-2.5 rounded text-sm font-semibold transition-all duration-150 active:scale-95"
            >
              Essential Only
            </button>
          </div>
        </div>
      </main>
      <FooterBar />
      <Toast message={toast.message} visible={toast.visible} />
    </div>
  );
}
